import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Box, IconButton, Popover, SxProps, Theme, Typography } from '@mui/material';
import { child, onValue, ref } from 'firebase/database';

import { useLanguage } from '../../hooks';
import { EmailOutline } from '../../icons';
import { useFirebaseStore } from '../../newStore/firebaseStore';
import { useUserDataStore } from '../../newStore/userDataStore';
import { commonStyles } from '../../style/CommonStyles';
import { formatDatascopeDate } from '../../utils/formatDatetime';
import { paths } from '../../utils/navigation';

const customStyles: Record<string, SxProps<Theme>> = {
  button: {
    border: 'solid 1px #3E8FFA',
    color: '#1F52B3',
    background: '#D8EFFE',
    '&:hover': {
      background: '#3E8FFA',
      color: '#FFFFFF',
    },
    '&.Mui-disabled': {
      color: '#B8BABF',
      borderColor: '#B8BABF',
      backgroundColor: '#F3F5F9',
    },
  },
  popover: {
    marginTop: '8px',
    '& .MuiPopover-paper': {
      borderRadius: '12px',
    },
  },
  popoverContent: {
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '300px',
    overflow: 'auto',
  },
  popoverItem: {
    cursor: 'pointer',
    padding: '3px 10px',
    borderRadius: '8px',
    backgroundColor: '#FEFDE5',
    maxWidth: '200px',
  },
  popoverItemText: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#3B3C40',
    lineHeight: '16px',
  },
  popoverItemDate: {
    fontSize: '12px',
    fontWeight: 400,
    opacity: 0.8,
    lineHeight: '18px',
  },
  badge: {
    backgroundColor: '#FA8C16',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    position: 'absolute',
    textAlign: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: '-5px',
    right: '-8px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#FFFFFF',
  },
};

interface ChatNotificationResponseItem {
  id: string;
  code: string;
  date: number;
  name: string;
  readed: boolean;
}

interface ChatNotification {
  formName: string;
  answerId: string;
  date: Date;
  read: boolean;
}

export function ChatNotifications() {
  const lang = useLanguage();

  const { currentUser, lang: langParam } = useUserDataStore();
  const { firebaseConfigured, user, rtDatabase } = useFirebaseStore();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);

  const unreadNotifications = notifications.filter(
    (notification) => !notification.read && !!notification.formName,
  );

  useEffect(() => {
    const getChat = async () => {
      if (firebaseConfigured && rtDatabase) {
        onValue(
          child(ref(rtDatabase), `datascope/messages/${user.uid}/USER${currentUser.id}`),
          (snapshot) => {
            const data = snapshot.val();
            if (data) {
              const notifications: ChatNotification[] = Object.entries(data).map(([id, value]) => {
                const notification = value as ChatNotificationResponseItem;
                return {
                  formName: `${notification.name} #${notification.code}`,
                  answerId: id,
                  date: new Date(notification.date),
                  read: notification.readed,
                };
              });
              setNotifications(notifications);
            }
          },
        );
      }
    };

    getChat();
  }, [firebaseConfigured]);

  return (
    <>
      <IconButton
        sx={customStyles.button}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={!unreadNotifications.length}
      >
        <EmailOutline
          fontSize='small'
          color='inherit'
        />
        {unreadNotifications.length > 0 && (
          <Typography
            component='span'
            sx={customStyles.badge}
          >
            {unreadNotifications.length}
          </Typography>
        )}
      </IconButton>
      <Popover
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={customStyles.popover}
      >
        <Box sx={customStyles.popoverContent}>
          {unreadNotifications.map((notification, index) => (
            <Link
              key={`${notification.answerId}-${index}`}
              style={commonStyles.linkOverride}
              to={paths.forms.oldAnswerDetail.replace(':answerId', notification.answerId)}
              onClick={() => {
                setAnchorEl(null);
              }}
            >
              <Box sx={customStyles.popoverItem}>
                <Typography sx={customStyles.popoverItemText}>
                  {lang.mainTopBar.newNotifications.replace('{formName}', notification.formName)}
                </Typography>
                {notification.date && (
                  <Typography sx={customStyles.popoverItemDate}>
                    {formatDatascopeDate(notification.date, 'date', langParam)}
                  </Typography>
                )}
              </Box>
            </Link>
          ))}
        </Box>
      </Popover>
    </>
  );
}
